// Pure DAG-shaping for the "Custom plans" breakdown's DAG rail (#4341) — turns
// a plan's `PlanProjectionGroup[]` (already loaded by `usePlan`, `lib/plan.ts`)
// into the ordered, humanised nodes the rail renders. No I/O; the projection
// itself is the fetch.

import type { PlanProjectionGroup } from '@developerz/domain';
import { TaskHoldReason } from '@developerz/domain';

/**
 * One DAG rail node. `hold` is null for a group with no `heldOn` — running,
 * terminal, or merely queued behind sibling work with no gate naming a reason.
 * `hold.labelKey` is always `fleet.plans.custom.held.<reason>`: EVERY member
 * of {@link TaskHoldReason} maps to a labelKey of that shape
 * (`lib/plan-dag.test.ts` proves the shape, iterating every member and
 * cross-checking the list against {@link TaskHoldReasonSchema} directly), and
 * a translation exists in `locales/en.json` for every one of them
 * (`i18n.test.ts`, "custom-plans labels cover every disposition, stall
 * reason, merge decision and held reason" — iterating
 * {@link TaskHoldReasonSchema} too, never a hand-kept list, so a member added
 * to the domain without catalog copy fails THAT seam instead of rendering
 * `⟦fleet.plans.custom.held.<reason>⟧` on the DAG rail).
 */
export type PlanDagNode = {
  groupId: string;
  position: number;
  title: string;
  disposition: PlanProjectionGroup['disposition'];
  dependsOn: readonly string[];
  hold: { labelKey: string; upstreamTitle: string | null } | null;
};

/**
 * The upstream group whose merge would lift an `upstream_group_unmerged` hold.
 * A group may depend on several siblings; the one still short of `merged` is
 * the one actually holding this row (`upstreamGroupUnmergedSql`'s own
 * predicate), so that is the title surfaced. If every dependency already
 * reads `merged` — a narrow redelivery window, the read racing the group's own
 * flip — fall back to the first dependency named, rather than reporting no
 * upstream at all for a hold that is, by construction, non-null.
 */
function resolveUpstreamTitle(
  group: PlanProjectionGroup,
  byId: ReadonlyMap<string, PlanProjectionGroup>,
): string | null {
  const deps = group.dependsOn.map((id) => byId.get(id)).filter((dep) => dep !== undefined);
  const first = deps[0];
  if (first === undefined) return null;
  const stillUnmerged = deps.find((dep) => dep.status !== 'merged');
  return (stillUnmerged ?? first).title;
}

/**
 * Groups ordered `(position, groupId)` — `groupId` is the tiebreak because
 * `position` is NOT unique (two groups can share a position;
 * `PlanProjectionGroupSchema` only requires it non-negative). Exported so the
 * DAG rail (via {@link planDagNodes}) and `PlanGroupTable.tsx` sort by the
 * SAME comparator instance rather than each hand-copying the tiebreak — two
 * independent literals can silently desync; importing one function cannot.
 */
export function orderedGroups<T extends Pick<PlanProjectionGroup, 'position' | 'groupId'>>(
  groups: readonly T[],
): T[] {
  return [...groups].sort(
    (a, b) =>
      a.position - b.position || (a.groupId < b.groupId ? -1 : a.groupId > b.groupId ? 1 : 0),
  );
}

/**
 * Groups ordered `(position, groupId)` via {@link orderedGroups}, then
 * humanised into rail nodes.
 */
export function planDagNodes(groups: readonly PlanProjectionGroup[]): PlanDagNode[] {
  const byId = new Map(groups.map((g) => [g.groupId, g] as const));
  const ordered = orderedGroups(groups);
  return ordered.map((group) => ({
    groupId: group.groupId,
    position: group.position,
    title: group.title,
    disposition: group.disposition,
    dependsOn: group.dependsOn,
    hold:
      group.heldOn === null
        ? null
        : {
            labelKey: `fleet.plans.custom.held.${group.heldOn.reason}`,
            upstreamTitle:
              group.heldOn.reason === TaskHoldReason.UpstreamGroupUnmerged
                ? resolveUpstreamTitle(group, byId)
                : null,
          },
  }));
}
